import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Chip,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Fab,
  CircularProgress,
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  CheckCircle,
  Schedule,
  Flag,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { useForm } from 'react-hook-form';
import { useAuth } from '../contexts/AuthContext';
import { useSubscription, FREE_LIMITS } from '../contexts/SubscriptionContext';
import { goalsService, edgeFunctionsService } from '../services/supabaseService';
import { Goal, Achievement, GoalCompletionResponse } from '../lib/supabase';
import toast from 'react-hot-toast';

const MotionCard = motion(Card);

interface GoalFormData {
  title: string;
  description: string;
  category: 'longevity' | 'healthspan' | 'wellness';
  priority: 'low' | 'medium' | 'high';
  target_date: string;
  time_required: number;
}

const Goals: React.FC = () => {
  const { user } = useAuth();
  const { isPremium } = useSubscription();
  const [goals, setGoals] = useState<Goal[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingGoal, setEditingGoal] = useState<Goal | null>(null);
  const [filter, setFilter] = useState<string>('all');

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<GoalFormData>();

  // Load goals from Supabase
  useEffect(() => {
    if (user) {
      loadGoals();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const loadGoals = async () => {
    if (!user) return;
    
    try {
      setLoading(true);
      const userGoals = await goalsService.getGoals(user.id);
      setGoals(userGoals);
    } catch (error: any) {
      toast.error('Failed to load goals: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateGoal = async (data: GoalFormData) => {
    if (!user) return;

    if (!isPremium && goals.length >= FREE_LIMITS.maxGoals) {
      toast.error(`Free users can only have ${FREE_LIMITS.maxGoals} goals. Upgrade to Premium for unlimited goals!`);
      return;
    }

    try {
      const newGoal = await goalsService.createGoal({
        user_id: user.id,
        title: data.title,
        description: data.description,
        category: data.category,
        priority: data.priority,
        target_date: data.target_date,
        time_required: data.time_required,
        progress: 0,
        status: 'active',
      });

      setGoals([newGoal, ...goals]);
      setDialogOpen(false);
      reset();
      toast.success('Goal created successfully!');
    } catch (error: any) {
      toast.error('Failed to create goal: ' + error.message);
    }
  };

  const handleEditGoal = async (data: GoalFormData) => {
    if (!editingGoal) return;

    try {
      const updatedGoal = await goalsService.updateGoal(editingGoal.id, {
        title: data.title,
        description: data.description,
        category: data.category,
        priority: data.priority,
        target_date: data.target_date,
        time_required: data.time_required,
      });

      setGoals(goals.map(goal => 
        goal.id === editingGoal.id ? updatedGoal : goal
      ));
      setDialogOpen(false);
      setEditingGoal(null);
      reset();
      toast.success('Goal updated successfully!');
    } catch (error: any) {
      toast.error('Failed to update goal: ' + error.message);
    }
  };

  const handleDeleteGoal = async (goalId: string) => {
    try {
      await goalsService.deleteGoal(goalId);
      setGoals(goals.filter(goal => goal.id !== goalId));
      toast.success('Goal deleted successfully!');
    } catch (error: any) {
      toast.error('Failed to delete goal: ' + error.message);
    }
  };

  const handleCompleteGoal = async (goalId: string) => {
    try {
      // Try to use Edge Function for enhanced completion with rewards
      try {
        const result: GoalCompletionResponse = await edgeFunctionsService.completeGoalWithRewards(goalId);
        
        // Update local state
        setGoals(goals.map(goal =>
          goal.id === goalId ? { ...goal, status: 'completed', progress: 100 } : goal
        ));
        
        // Show success message with rewards
        toast.success(result.message);
        
        // Handle achievements
        if (result.rewards.newAchievements.length > 0) {
          result.rewards.newAchievements.forEach((achievement: Achievement) => {
            toast.success(`🏆 Achievement Unlocked: ${achievement.name}!`, { duration: 5000 });
          });
        }
        
        // Handle level up
        if (result.rewards.levelUp.leveledUp) {
          toast.success(`🆙 Level Up! You're now level ${result.rewards.levelUp.newLevel}!`, { duration: 5000 });
        }
        
      } catch (edgeError) {
        console.warn('Edge function unavailable, using fallback:', edgeError);
        
        // Fallback to basic goal completion
        const updatedGoal = await goalsService.updateGoal(goalId, {
          status: 'completed',
          progress: 100,
        });

        setGoals(goals.map(goal =>
          goal.id === goalId ? updatedGoal : goal
        ));
        toast.success('Congratulations! Goal completed! 🎉');
      }
    } catch (error: any) {
      toast.error('Failed to complete goal: ' + error.message);
    }
  };

  const openCreateDialog = () => {
    setEditingGoal(null);
    reset();
    setDialogOpen(true);
  };

  const openEditDialog = (goal: Goal) => {
    setEditingGoal(goal);
    reset({
      title: goal.title,
      description: goal.description,
      category: goal.category,
      priority: goal.priority,
      target_date: goal.target_date,
      time_required: goal.time_required,
    });
    setDialogOpen(true);
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'longevity': return '#9c27b0';
      case 'healthspan': return '#00bcd4';
      case 'wellness': return '#4caf50';
      default: return '#6b7280';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high': return '#ef4444';
      case 'medium': return '#f59e0b';
      case 'low': return '#10b981';
      default: return '#6b7280';
    }
  };

  const filteredGoals = goals.filter(goal => {
    if (filter === 'all') return true;
    if (filter === 'active') return goal.status === 'active';
    if (filter === 'completed') return goal.status === 'completed';
    return goal.category === filter;
  });

  const onSubmit = editingGoal ? handleEditGoal : handleCreateGoal;

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h3" component="h1" sx={{ mb: 2, fontWeight: 'bold' }}>
          Goals Management
        </Typography>
        <Typography variant="h6" color="text.secondary" sx={{ mb: 3 }}>
          Track and manage your personal development goals
        </Typography>

        <Box sx={{ display: 'flex', gap: 2, mb: 4, flexWrap: 'wrap' }}>
          <Chip
            label="All"
            onClick={() => setFilter('all')}
            color={filter === 'all' ? 'primary' : 'default'}
            variant={filter === 'all' ? 'filled' : 'outlined'}
          />
          <Chip
            label="Active"
            onClick={() => setFilter('active')}
            color={filter === 'active' ? 'primary' : 'default'}
            variant={filter === 'active' ? 'filled' : 'outlined'}
          />
          <Chip
            label="Completed"
            onClick={() => setFilter('completed')}
            color={filter === 'completed' ? 'primary' : 'default'}
            variant={filter === 'completed' ? 'filled' : 'outlined'}
          />
          <Chip
            label="Longevity"
            onClick={() => setFilter('longevity')}
            color={filter === 'longevity' ? 'primary' : 'default'}
            variant={filter === 'longevity' ? 'filled' : 'outlined'}
          />
          <Chip
            label="Healthspan"
            onClick={() => setFilter('healthspan')}
            color={filter === 'healthspan' ? 'primary' : 'default'}
            variant={filter === 'healthspan' ? 'filled' : 'outlined'}
          />
          <Chip
            label="Wellness"
            onClick={() => setFilter('wellness')}
            color={filter === 'wellness' ? 'primary' : 'default'}
            variant={filter === 'wellness' ? 'filled' : 'outlined'}
          />
        </Box>

        {!isPremium && (
          <Box sx={{ mb: 3, p: 2, backgroundColor: 'rgba(255, 215, 0, 0.1)', borderRadius: 2 }}>
            <Typography variant="body2">
              📝 Free users can have up to {FREE_LIMITS.maxGoals} goals. You have {goals.length}/{FREE_LIMITS.maxGoals} goals.
              Upgrade to Premium for unlimited goals!
            </Typography>
          </Box>
        )}
      </Box>

      <Grid container spacing={3}>
        {filteredGoals.map((goal, index) => (
          <Grid item xs={12} md={6} lg={4} key={goal.id}>
            <MotionCard
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                opacity: goal.status === 'completed' ? 0.8 : 1,
              }}
            >
              <CardContent sx={{ flexGrow: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', flexGrow: 1 }}>
                    {goal.title}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <IconButton size="small" onClick={() => openEditDialog(goal)}>
                      <Edit fontSize="small" />
                    </IconButton>
                    <IconButton size="small" onClick={() => handleDeleteGoal(goal.id)}>
                      <Delete fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>

                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {goal.description}
                </Typography>

                <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                  <Chip
                    size="small"
                    label={goal.category}
                    sx={{ backgroundColor: getCategoryColor(goal.category), color: 'white' }}
                  />
                  <Chip
                    size="small"
                    label={goal.priority}
                    icon={<Flag />}
                    sx={{ backgroundColor: getPriorityColor(goal.priority), color: 'white' }}
                  />
                  <Chip
                    size="small"
                    label={`${goal.time_required}h/week`}
                    icon={<Schedule />}
                    variant="outlined"
                  />
                </Box>

                <Box sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                      Progress
                    </Typography>
                    <Typography variant="body2">
                      {goal.progress}%
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={goal.progress}
                    sx={{
                      height: 8,
                      borderRadius: 4,
                      '& .MuiLinearProgress-bar': {
                        backgroundColor: getCategoryColor(goal.category),
                      },
                    }}
                  />
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="caption" color="text.secondary">
                    Target: {new Date(goal.target_date).toLocaleDateString()}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Status: {goal.status}
                  </Typography>
                </Box>

                {goal.status === 'active' && goal.progress < 100 && (
                  <Button
                    variant="contained"
                    fullWidth
                    startIcon={<CheckCircle />}
                    onClick={() => handleCompleteGoal(goal.id)}
                    sx={{ mt: 'auto' }}
                  >
                    Mark Complete
                  </Button>
                )}

                {goal.status === 'completed' && (
                  <Chip
                    label="✅ Completed"
                    color="success"
                    sx={{ width: '100%', mt: 'auto' }}
                  />
                )}
              </CardContent>
            </MotionCard>
          </Grid>
        ))}
      </Grid>

      {filteredGoals.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
            No goals found
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Create your first goal to start tracking your progress!
          </Typography>
        </Box>
      )}

      {/* Floating Action Button */}
      <Fab
        color="primary"
        aria-label="add goal"
        sx={{ position: 'fixed', bottom: 16, right: 16 }}
        onClick={openCreateDialog}
      >
        <Add />
      </Fab>

      {/* Goal Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingGoal ? 'Edit Goal' : 'Create New Goal'}
        </DialogTitle>
        <DialogContent>
          <Box component="form" sx={{ mt: 2 }}>
            <TextField
              fullWidth
              label="Goal Title"
              margin="normal"
              {...register('title', { required: 'Title is required' })}
              error={!!errors.title}
              helperText={errors.title?.message}
            />
            <TextField
              fullWidth
              label="Description"
              margin="normal"
              multiline
              rows={3}
              {...register('description', { required: 'Description is required' })}
              error={!!errors.description}
              helperText={errors.description?.message}
            />
            <FormControl fullWidth margin="normal">
              <InputLabel>Category</InputLabel>
              <Select
                {...register('category', { required: 'Category is required' })}
                error={!!errors.category}
              >
                <MenuItem value="longevity">Longevity</MenuItem>
                <MenuItem value="healthspan">Healthspan</MenuItem>
                <MenuItem value="wellness">Wellness</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth margin="normal">
              <InputLabel>Priority</InputLabel>
              <Select
                {...register('priority', { required: 'Priority is required' })}
                error={!!errors.priority}
              >
                <MenuItem value="low">Low</MenuItem>
                <MenuItem value="medium">Medium</MenuItem>
                <MenuItem value="high">High</MenuItem>
              </Select>
            </FormControl>
            <TextField
              fullWidth
              label="Target Date"
              type="date"
              margin="normal"
              InputLabelProps={{ shrink: true }}
              {...register('target_date', { required: 'Target date is required' })}
              error={!!errors.target_date}
              helperText={errors.target_date?.message}
            />
            <TextField
              fullWidth
              label="Time Required (hours per week)"
              type="number"
              margin="normal"
              {...register('time_required', {
                required: 'Time required is required',
                min: { value: 1, message: 'Minimum 1 hour per week' },
                max: { value: 40, message: 'Maximum 40 hours per week' },
              })}
              error={!!errors.time_required}
              helperText={errors.time_required?.message}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSubmit(onSubmit)}>
            {editingGoal ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Goals; 